/**
 * One id per scouted match, carried by every copy of it.
 *
 * The id is minted once per match in Scout.js and travels inside the match itself: the
 * saved copy (`<gamemode>_match_<n>`), the QR code, and from there the queue. The queue
 * uses it as the record's `id`, which the Octopus Pi uses as `submission_id` to tell a
 * resend from a new match. So the same match reaching the Pi twice - exported on the
 * scout's laptop and scanned from its QR on another, or rebuilt from the saved copy -
 * is stored once.
 *
 * Firebase never sees it: uploadObject and the Relay tab delete it, like `version`.
 * Neptune reads every field of a record (rawData.js `processMatch`), so a field that
 * reached Firestore would show up there as a column.
 */

export const SUBMISSION_ID_KEY = "submission_id";

export function newSubmissionId() {
    return crypto.randomUUID();
}

/**
 * The match with its id added. A match that already has one - a saved copy, a scanned
 * QR - keeps it, since replacing it would make that match look new to the Pi.
 * @param {Map} map the match's dataMap
 * @param {string} submissionId
 * @returns {Map} a copy, or the same map when there is nothing to add
 */
export function withSubmissionId(map, submissionId) {
    if (!(map instanceof Map) || !submissionId || map.get(SUBMISSION_ID_KEY)) return map;
    const copy = new Map(map);
    copy.set(SUBMISSION_ID_KEY, submissionId);
    return copy;
}

/**
 * When a match was last edited, carried like `submission_id`: in the saved copy and the QR
 * once someone edits it (the Scanner's text box), and as `_edited_at` on a queued record
 * (the Relay tab's edit). An edited record is a correction: it may overwrite Firebase, and
 * the Pi stores it as a new version (octopus.js `piKey`). An unedited one never overwrites.
 * Like the id, it never reaches Firebase as a field.
 */
export const EDITED_AT_KEY = "edited_at";
